// src/App.js
import { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import Carousel from "bootstrap/js/dist/carousel";

const API_BASE = (process.env.REACT_APP_API_BASE || "/api").replace(/\/+$/, "");

function App() {
  const styles = `
    :root{ --navy:#1f2a3b; --brand:#6ea8fe; }

    /* Background carousel */
    .bg-carousel{ position:fixed; inset:0; z-index:-2; }
    .bg-carousel .carousel,
    .bg-carousel .carousel-inner,
    .bg-carousel .carousel-item{ height:100%; }
    .bg-carousel .carousel-item{
      display:flex; align-items:center; justify-content:center;
    }
    .bg-carousel .carousel-item img{
      width:100%; height:100%; object-fit:cover; object-position:center;
      filter:saturate(1.05) contrast(1.05);
    }
    /* Navy scrim */
    .bg-scrim{
      position:fixed; inset:0; z-index:-1;
      background: radial-gradient(1200px 600px at 70% 20%, #00000040, #00000080),
                  linear-gradient(180deg, #0c1220a6, #0c1220cc);
      pointer-events:none;
    }

    /* Centered auth */
    .auth-center{ min-height:100vh; display:flex; align-items:center; justify-content:center; padding:24px; }

    /* Glass card */
    .wrapper{
      width:100%; max-width:520px;
      background: rgba(255,255,255,.08);
      border:1px solid #ffffff26; border-radius:16px; padding:28px;
      backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px);
      color:#e9f1fb; box-shadow:0 10px 30px rgba(0,0,0,.35);
    }
    .brand-row{ display:flex; align-items:center; gap:12px; margin-bottom:18px; }
    .brand-logo{ width:40px; height:40px; border-radius:12px; display:grid; place-items:center; background:#2a3b59; color:#e9f1fb; box-shadow: inset 0 1px 0 #ffffff22; }
    .brand-title{ margin:0; font-size:22px; font-weight:700; letter-spacing:.2px; }

    .error-message{ color:#ff9aa2; font-size:.95rem; margin-bottom:10px; }

    .input-box{ position:relative; margin-bottom:14px; }
    .input-box input{
      width:100%; border:1px solid #ffffff2b; background:#0f1627b3; color:#e9f1fb;
      border-radius:10px; padding:12px 44px 12px 14px; outline:none;
    }
    .input-box input::placeholder{ color:#c0cbe0; }
    .input-box input:focus{ border-color:var(--brand); box-shadow:0 0 0 .2rem #6ea8fe33; }

    /* Static right-side icon for email */
    .input-icon{
      position:absolute; right:12px; top:50%; transform:translateY(-50%);
      font-size:20px; color:#c0cbe0;
    }

    /* Toggle password visibility button */
    .toggle-visibility{
      position:absolute; right:8px; top:50%; transform:translateY(-50%);
      width:36px; height:36px; border-radius:8px; border:0; background:transparent;
      color:#c0cbe0; display:grid; place-items:center; cursor:pointer;
    }
    .toggle-visibility:hover{ background:#ffffff1a; color:#fff; }
    .toggle-visibility:focus-visible{ outline:none; box-shadow:0 0 0 .2rem #6ea8fe66; }

    .remember-forgot{ display:flex; align-items:center; justify-content:space-between; font-size:.95rem; margin:10px 0 16px; color:#c8d3e8; }
    .remember-forgot a{ color:#e9f1fb; text-decoration:underline; opacity:.9; }
    .remember-forgot a:hover{ opacity:1; }

    .btn-login{ width:100%; border-radius:10px; padding:10px 14px; font-weight:600; box-shadow:0 6px 18px rgba(13,110,253,.35); }
    .auth-note{ color:#c8d3e8; font-size:.85rem; text-align:center; margin-top:12px; }

    .carousel-indicators [data-bs-target]{ width:8px; height:8px; border-radius:50%; }
    .carousel-indicators .active{ background:#ffffff; }

    @media (max-width:420px){ .wrapper{ padding:20px; } }
  `;

  const IMAGES = [
    "https://images.pexels.com/photos/1238864/pexels-photo-1238864.jpeg?auto=compress&cs=tinysrgb&w=1600",
    "https://images.pexels.com/photos/534220/pexels-photo-534220.jpeg?auto=compress&cs=tinysrgb&w=1600",
    "https://images.pexels.com/photos/5505124/pexels-photo-5505124.jpeg?auto=compress&cs=tinysrgb&w=1600",
  ];

  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState(localStorage.getItem("rememberEmail") || "");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(!!localStorage.getItem("rememberEmail"));
  const [loginError, setLoginError] = useState("");
  const [loading, setLoading] = useState(false);

  // 🔐 Visiting "/" acts as logout: clear auth-related keys
  useEffect(() => {
    const remembered = localStorage.getItem("rememberEmail");
    // Remove only auth/user keys (keep rememberEmail)
    ["token", "email", "name", "organization", "userId", "role", "expiresAt"].forEach((k) =>
      localStorage.removeItem(k)
    );
    if (remembered) localStorage.setItem("rememberEmail", remembered);
    // If you ever stored auth in sessionStorage, clear it too
    try {
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("email");
    } catch {}
  }, []);

  // Auto-cycling carousel (no pause on hover)
  useEffect(() => {
    const el = document.getElementById("bgCarousel");
    if (!el) return;
    const existing = Carousel.getInstance(el);
    const instance =
      existing ||
      new Carousel(el, {
        interval: 4000,
        ride: "carousel",
        pause: false,
        wrap: true,
        touch: true,
        keyboard: false,
      });
    instance.cycle();
  }, []);

  const emailValid = /\S+@\S+\.\S+/.test(email);
  const passwordValid = password.length > 0;

  const loginSubmit = async (e) => {
    e.preventDefault();
    setLoginError("");

    if (!emailValid || !passwordValid) {
      setLoginError("Please enter a valid email and password.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Invalid email or password");

      // Store token for route guard; store email if your app needs it later
      if (data?.token) localStorage.setItem("token", data.token);
      const user = data?.user || {};
      localStorage.setItem("email", user.email || email);
      if (user.name) localStorage.setItem("name", user.name);
      if (user.organization) localStorage.setItem("organization", user.organization);
      if (user.id != null) localStorage.setItem("userId", String(user.id));
      if (user.role) localStorage.setItem("role", user.role);

      if (remember) localStorage.setItem("rememberEmail", email);
      else localStorage.removeItem("rememberEmail");

      window.location.href = data?.redirect || "/Homepage";
    } catch (err) {
      setLoginError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{styles}</style>

      {/* Background carousel */}
      <div className="bg-carousel">
        <div
          id="bgCarousel"
          className="carousel slide carousel-fade h-100"
          data-bs-ride="carousel"
          data-bs-interval="4000"
          data-bs-pause="false"
          data-bs-touch="true"
          data-bs-wrap="true"
        >
          <div className="carousel-inner h-100">
            {IMAGES.map((src, i) => (
              <div key={i} className={`carousel-item h-100 ${i === 0 ? "active" : ""}`}>
                <img src={src} alt={`Slide ${i + 1}`} />
              </div>
            ))}
          </div>

          <button className="carousel-control-prev" type="button" data-bs-target="#bgCarousel" data-bs-slide="prev">
            <span className="carousel-control-prev-icon" aria-hidden="true" />
            <span className="visually-hidden">Previous</span>
          </button>
          <button className="carousel-control-next" type="button" data-bs-target="#bgCarousel" data-bs-slide="next">
            <span className="carousel-control-next-icon" aria-hidden="true" />
            <span className="visually-hidden">Next</span>
          </button>

          <div className="carousel-indicators">
            {IMAGES.map((_, i) => (
              <button
                key={i}
                type="button"
                data-bs-target="#bgCarousel"
                data-bs-slide-to={i}
                className={i === 0 ? "active" : ""}
                aria-current={i === 0 ? "true" : undefined}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Readability scrim */}
      <div className="bg-scrim" />

      {/* Centered login */}
      <main className="auth-center container-xxl">
        <div className="wrapper">
          <div className="brand-row">
            <div className="brand-logo">
              <i className="bi bi-airplane-engines-fill"></i>
            </div>
            <h1 className="brand-title">Terra Progress</h1>
          </div>

          {loginError && <div className="error-message">{loginError}</div>}

          <form onSubmit={loginSubmit} noValidate>
            <div className="input-box">
              <input
                type="email"
                placeholder="Email address"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                aria-label="Email address"
              />
              <i className="bi bi-person-circle input-icon" aria-hidden="true"></i>
            </div>

            <div className="input-box">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                aria-label="Password"
              />
              <button
                type="button"
                className="toggle-visibility"
                aria-label={showPassword ? "Hide password" : "Show password"}
                aria-pressed={showPassword}
                onClick={() => setShowPassword((v) => !v)}
              >
                <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`} />
              </button>
            </div>

            <div className="remember-forgot">
              <label className="d-flex align-items-center gap-2">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                Remember me
              </label>
              <a href="/ForgotPassword">Forgot password?</a>
            </div>

            <button type="submit" className="btn btn-primary btn-login" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Logging in...
                </>
              ) : (
                "Login"
              )}
            </button>
          </form>

          <div className="auth-note">Use your Terra account to continue.</div>
        </div>
      </main>
    </>
  );
}

export default App;
